import React, { FC, forwardRef, useState, useEffect, Ref } from 'react';
import styled from 'styled-components';

type SwitchProps = {
  onChange?: (value: boolean) => void;
  defaultValue?: boolean;
  isDisabled?: boolean;
  name?: string;
  testId?: string;
  ref?: Ref<HTMLButtonElement>;
};

const Switch: FC<SwitchProps> = forwardRef(
  (
    { onChange, isDisabled, name, defaultValue = false, testId = 'switch' },
    ref,
  ) => {
    const [state, setState] = useState<boolean>(defaultValue);

    useEffect(() => {
      setState(defaultValue);
    }, [setState, defaultValue]);

    const handleSwitchChange = () => {
      const nextState = !state;
      setState(nextState);

      if (onChange) {
        onChange(nextState);
      }
    };

    return (
      <SwitchComponent
        data-test-id={testId}
        role="switch"
        aria-checked={state}
        name={name}
        value={state?.toString()}
        aria-readonly={isDisabled}
        disabled={isDisabled}
        onClick={handleSwitchChange}
        ref={ref}
      />
    );
  },
);

Switch.displayName = 'Switch';

const SwitchComponent = styled.button.attrs({ type: 'button' })`
  position: relative;
  display: inline-block;
  height: 2em;
  width: 3.5em;
  font: inherit;
  box-sizing: border-box;
  border: 1px solid #bebebe; /* TODO: Theme */
  border-radius: 1em;
  background-color: #f5f5f8; /* TODO: Theme */
  transition: background 0.2s ease-in;
  cursor: pointer;

  &:before,
  &:after {
    content: ' ';
    position: absolute;
    transition: left 0.2s ease-in;
  }

  &:before {
    left: 0.125em;
    top: 0.175em;
    width: 1.5em;
    height: 1.5em;
    border-radius: 50%;
    box-shadow: 0 2px 4px 0 #00000080; /* TODO: Theme */
    background-color: #fff; /* TODO: Theme */
    cursor: pointer;
  }

  &:after {
    content: ' ';
    visibility: hidden;
    left: 0.75em;
    top: 0.5em;
    width: 0.25em;
    height: 0.625em;
    border: solid #157efb; /* TODO: Theme */
    border-width: 0 0.125em 0.125em 0;
    transform: rotate(45deg);
  }

  &[aria-checked='true'] {
    border-color: #157efb; /* TODO: Theme */
    background-color: #157efb; /* TODO: Theme */

    &:before {
      left: 1.75em;
    }

    &:after {
      left: 2.35em;
      visibility: visible;
    }
  }

  &[disabled] {
    border-color: #bebebe; /* TODO: Theme */
    background-color: #ececf0; /* TODO: Theme */
    cursor: not-allowed;

    &:before,
    &:after {
      border-color: #bebebe; /* TODO: Theme */
      cursor: not-allowed;
    }

    &:before {
      background-color: #f5f5f8; /* TODO: Theme */
      box-shadow: 0 2px 4px 0 #0000004d; /* TODO: Theme */
    }
  }
`;

export { Switch };
